import { createMutationFanout, type AppliedOp, type BlockMutationCallback } from '@w/crdt'
import { s } from '@w/schema'
import type { sql } from '@w/db'
import { cardDerivationStatements, receivedCardBatchStatements, retainedPartStatements, type DerivationArgs } from './card-derivation'
import { pageRelationStatements } from './page-relations-derivation'

const appliedRowSchema = s.object({
  kind: s.string(), node: s.string(), entity: s.string().nullable(), content: s.string().nullable(), key: s.string().nullable(), oldValue: s.string().nullable(), value: s.string().nullable(), qid: s.string().nullable(),
  fields: s.transform(s.string(), value => s.object({ type: s.string().optional(), pageId: s.string().optional(), src: s.string().nullable().optional() }).parse(JSON.parse(value))),
})
export function parseMutationCdc(json: string) { return s.array(appliedRowSchema).parse(JSON.parse(json)) }

/** SQL consumes the same mutation's transaction CDC; callbacks select the product row writes to author. */
type Context = { derivation: DerivationArgs; created: boolean }
type Write = ReturnType<typeof sql>
const questions: BlockMutationCallback<Write, Context> = (events, { derivation, created }) => events.some(e => e.kind === 'insert' || e.kind === 'remove' || e.kind === 'move' || (e.kind === 'set' && ['fractionalIndex','importance','difficulty','subtype','tta'].includes(e.key ?? '')))
  ? [...cardDerivationStatements({ kind: created ? 'page' : 'applied' }, derivation), ...retainedPartStatements(derivation)] : []
const links: BlockMutationCallback<Write, Context> = (_events, { derivation, created }) => pageRelationStatements(created ? 'lifecycle' : 'update', derivation.pageId)
const fanout = createMutationFanout<Write, Context>({ question: questions, cloze: questions, rule: questions, pageLink: links })

export function authorMutationWrites(events: readonly AppliedOp[], derivation: DerivationArgs, created = false) {
  return fanout(events, { derivation, created })
}

type ReceivedRow = {pageId:string;updatedAt:number;editedBy:string;event:AppliedOp}
const receivedQuestions: BlockMutationCallback<Write, readonly ReceivedRow[]> = (events, rows) => {
  const questions = new Set(events)
  const affected = rows.filter(row=>questions.has(row.event) && (row.event.kind==='insert' || row.event.kind==='remove' || row.event.kind==='move' || (row.event.kind==='set' && ['fractionalIndex','importance','difficulty','subtype','tta'].includes(row.event.key ?? '')))).map(row=>({
    pageId:row.pageId,qid:row.event.qid,inserted:row.event.kind==='insert'?1:0,
    updatedAt:row.updatedAt,editedBy:row.editedBy,
  }))
  return affected.length > 0 ? receivedCardBatchStatements(JSON.stringify(affected)) : []
}
const receiveFanout = createMutationFanout<Write, readonly ReceivedRow[]>({question:receivedQuestions,cloze:receivedQuestions,rule:receivedQuestions})
const appliedEventsSchema=s.array(s.unknown())
export function receivedMutationBatchWrites(batches:readonly {pageId:string;updatedAt:number;editedBy:string;events:string}[]) {
  const rows=batches.flatMap(({events,...context})=>appliedEventsSchema.parse(JSON.parse(events)).flatMap(value=> {
    if(typeof value==='object' && value!==null && 'qid' in value && value.qid===null) return []
    return [{...context,event:appliedRowSchema.parse(value)}]
  }))
  return receiveFanout(rows.map(row=>row.event),rows)
}

/** One already decoded mutation uses the same batch writer as a received apply. */
export function receivedMutationWrites(events:readonly AppliedOp[], context:DerivationArgs & {events:string}) {
  return receiveFanout(events,events.map(event=>({pageId:context.pageId,updatedAt:context.updatedAt,editedBy:context.userId,event})))
}
